/**
 * entity_rename - Rename Entity Command
 * Allows AI to rename documents, folders, sheets, presentations, notes, etc.
 */

import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";
import { entityResolver } from "../../services/entityResolver";

const log = createFileLogger('ENTITY_RENAME');

export const entityRenameCommand: SelfContainedCommand = {
  id: 'entity_rename',
  
  description: `Rename any entity (document, folder, sheet, presentation, note, canvas, etc.)

**SUPPORTED ENTITIES:**
- Documents (doc_*)
- Folders (group_*)
- Sheets (sheet_*)
- Presentations (presentation_*)
- Notes (note_*)
- Canvas (canvas_*)
- Files (file_*, pdf_*)

**REQUIREMENTS:**
- entityId: Valid entity ID (llmId format or MongoDB ID)
- newName: Non-empty, max 200 characters
- Entity must exist and not be trashed

**EXAMPLES:**

Rename document:
{
  "entityId": "doc_red_apple_blue_sky",
  "newName": "Q4 2025 Report"
}

Rename folder:
{
  "entityId": "group_project_alpha",
  "newName": "Archive - Project Alpha"
}

Rename sheet:
{
  "entityId": "sheet_budget_2026",
  "newName": "Budget Forecast 2026"
}
`,
  
  autonomyLevel: 'ask',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'ID of entity to rename (llmId or MongoDB ID)',
        required: true,
        examples: [
          'doc_wild_mountain_calm_river',
          'group_research_papers',
          'sheet_budget_2026',
          'presentation_q4_review'
        ]
      },
      {
        name: 'newName',
        type: 'string',
        description: 'New name for the entity (max 200 characters)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'New name cannot be empty';
          if (value.length > 200) return 'Name too long (max 200 characters)';
          return null;
        },
        examples: [
          'Research Paper Draft',
          'Q4 2024 Reports',
          'Project Alpha Notes',
          'Budget Forecast 2026'
        ]
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { entityId: 'doc_abc_def', newName: 'Updated Report Title' },
        expectedOutput: createSuccessResult('Renamed entity', {
          entityId: 'doc_abc_def',
          oldName: 'Old Report Title',
          newName: 'Updated Report Title'
        }),
        description: 'Rename a document'
      }
    ],
    bad: [
      {
        input: { entityId: 'doc_abc', newName: '' },
        error: 'New name cannot be empty',
        why: 'Empty names are not allowed',
        fix: 'Provide a valid name'
      }
    ]
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },
  
  formatViewData: (result: any, input: any) => {
    if (!result || result.status !== 'success') {
      return undefined;
    }
    
    const data = result.data || {};
    return {
      type: 'entity_rename',
      summary: result.message || `Renamed to "${data.newName}"`,
      data: {
        entityId: data.entityId,
        oldName: data.oldName,
        newName: data.newName,
        entityType: data.entityType
      }
    };
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    let { entityId, newName } = params;
    
    try {
      // Trim new name
      newName = newName.trim();
      
      log.info('[entity_rename] Starting rename:', { entityId, newName });
      
      // Get entity from store
      const entityStore = useEntityStore.getState();
      let entity = entityStore.entities.find(e => e._id === entityId || (e as any).llmId === entityId);
      
      // If not found by direct match, try resolving
      if (!entity) {
        const resolved = await entityResolver.resolveEntity(entityId, context);
        entity = resolved?.entity;
      }
      
      if (!entity) {
        return createErrorResult(
          'Entity not found',
          `No entity found with ID: ${entityId}`,
          'validation',
          { data: { providedId: entityId } }
        );
      }
      
      const resolvedId = entity._id;
      
      log.info('[entity_rename] Entity resolved:', {
        providedId: entityId,
        resolvedId,
        entityType: entity.entityType,
        currentName: entity.name
      });
      
      // Check if entity is trashed
      if ((entity as any).trashed === true) {
        return createErrorResult(
          'Cannot rename trashed entity',
          `Entity "${entity.name}" is in trash`,
          'validation',
          { data: { entityId: resolvedId, entityName: entity.name } }
        );
      }
      
      // Check if entity type is renameable
      const renameableTypes = [
        'Group', 'Doc', 'Sheet', 'Presentation', 'Note', 'Canvas',
        'File', 'ImageFile', 'VideoFile', 'AudioFile', 'StoryCharacter'
      ];
      
      if (!renameableTypes.includes(entity.entityType)) {
        return createErrorResult(
          'Entity type not renameable',
          `Cannot rename entities of type: ${entity.entityType}`,
          'validation',
          { data: { entityId: resolvedId, entityType: entity.entityType } }
        );
      }
      
      const oldName = entity.name;
      
      // Prepare update data
      const updateData: any = {
        name: newName
      };
      
      // Special handling for Group entities (folders)
      if (entity.entityType === 'Group') {
        updateData.skeleton = {
          ...entity.skeleton,
          groupName: newName
        };
      }
      
      // Perform rename via entity store
      await useEntityStore.getState().actions.updateEntity(resolvedId, updateData);
      
      log.info('[entity_rename] ✅ Entity renamed successfully:', {
        entityId: resolvedId,
        oldName,
        newName,
        entityType: entity.entityType
      });
      
      return createSuccessResult(
        `Renamed "${oldName}" to "${newName}"`,
        {
          entityId: resolvedId,
          oldName,
          newName,
          entityType: entity.entityType
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            affectedEntities: [resolvedId]
          },
          ui: {
            refreshContext: true
          }
        }
      );
      
    } catch (error: any) {
      log.error('[entity_rename] Error:', error);
      return createErrorResult(
        'Failed to rename entity',
        `Rename failed: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { entityId, newName },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

